import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Download, PackageSearch, Users, Mail, FileText, Search, Send, Copy, Upload, Radar, BadgeCheck, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MOON } from "@/content/moon";
import { useLang } from "@/lib/lang";

const MOON_STATUS = ["nouvelle", "en_analyse", "recherche_fournisseur", "devis_recu", "devis_envoye", "acceptee", "commande_en_cours", "livree", "annulee"];
const INQ_STATUS = ["brouillon", "en_attente_validation", "prete_a_envoyer", "envoyee", "reponse_recue", "relance_necessaire", "sans_reponse", "refusee", "terminee"];
const PART_STATUS = ["prospect", "partenaire", "en_pause", "refuse"];
const VERIF_STATUS = ["a_verifier", "verifie", "a_revoir"];

const CAT_LABELS = Object.fromEntries(MOON.categories.map((c) => [c.slug, c.label]));

function LeadsTab() {
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    api.get("/admin/leads").then(({ data }) => setLeads(data)).catch(() => setLeads([]));
  }, []);
  const setStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    setLeads(leads.map((l) => (l.id === id ? { ...l, status } : l)));
  };
  if (!leads) return <p className="text-sm text-steel">…</p>;
  if (leads.length === 0) return <p className="text-sm text-steel" data-testid="admin-leads-empty">Aucune demande reçue pour le moment.</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-line" data-testid="admin-leads-table">
      <table className="w-full min-w-[900px] text-left text-sm">
        <thead className="bg-paper text-xs uppercase tracking-wider text-steel">
          <tr>
            <th className="px-4 py-3">Date</th><th className="px-4 py-3">Nom</th><th className="px-4 py-3">Entreprise</th>
            <th className="px-4 py-3">Courriel</th><th className="px-4 py-3">Service</th><th className="px-4 py-3">Besoin</th>
            <th className="px-4 py-3">Statut</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} className="border-t border-line align-top" data-testid={`admin-lead-row-${l.id}`}>
              <td className="px-4 py-3 text-steel">{new Date(l.created_at).toLocaleString("fr-CA")}</td>
              <td className="px-4 py-3 font-medium text-navy-800">{l.first_name} {l.last_name}</td>
              <td className="px-4 py-3">{l.company}{l.source === "assistant_ia" && <span className="ml-1 rounded-full bg-badge px-2 py-0.5 text-[10px] font-bold text-sky-700">IA</span>}</td>
              <td className="px-4 py-3"><a className="text-electric underline" href={`mailto:${l.email}`}>{l.email}</a>{l.phone && <span className="block text-xs text-steel">{l.phone}</span>}</td>
              <td className="px-4 py-3">{l.service_label}</td>
              <td className="max-w-[280px] px-4 py-3 text-graphite">
                {l.message.length > 140 ? `${l.message.slice(0, 140)}…` : l.message}
                {(l.budget || l.deadline) && <span className="mt-1 block text-xs text-steel">{[l.budget, l.deadline].filter(Boolean).join(" · ")}</span>}
              </td>
              <td className="px-4 py-3">
                <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} data-testid={`admin-lead-status-${l.id}`}
                  className="rounded border border-line bg-white px-2 py-1 text-xs">
                  {["nouveau", "en_cours", "traite"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EquipmentTab() {
  const { t, lang } = useLang();
  const [requests, setRequests] = useState(null);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [matchFor, setMatchFor] = useState(null); // request_number with open match panel
  const [matches, setMatches] = useState(null);
  const [selected, setSelected] = useState([]);
  const [matchError, setMatchError] = useState("");

  const load = () => api.get("/admin/equipment-requests").then(({ data }) => setRequests(data)).catch(() => setRequests([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (num, status) => {
    await api.patch(`/admin/equipment-requests/${num}`, { status });
    setRequests(requests.map((r) => (r.request_number === num ? { ...r, status } : r)));
  };

  const openMatch = async (num) => {
    setMatchFor(matchFor === num ? null : num);
    setMatches(null);
    setSelected([]);
    setMatchError("");
    if (matchFor !== num) {
      try {
        const { data } = await api.post("/admin/match-suppliers", { request_number: num });
        setMatches(data);
      } catch (e) {
        setMatchError(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
  };

  const prepareInquiries = async (num) => {
    setMatchError("");
    try {
      const { data } = await api.post("/admin/supplier-inquiries", { request_number: num, supplier_ids: selected });
      alert(`${data.created.length} demande(s) de prix préparée(s) — voir l'onglet « Demandes de prix » pour valider et envoyer.`);
      setMatchFor(null);
    } catch (e) {
      setMatchError(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!requests) return <p className="text-sm text-steel">…</p>;
  const visible = requests.filter(
    (r) => (!filter || r.status === filter) &&
      (!search || `${r.request_number} ${r.company} ${r.contact_name} ${r.email} ${r.product}`.toLowerCase().includes(search.toLowerCase()))
  );
  return (
    <div data-testid="admin-equipment-table">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={lang === "fr" ? "Rechercher (n°, entreprise, produit…)" : "Search (no., company, product…)"} data-testid="admin-equipment-search"
          className="w-72 rounded-full border border-line bg-white px-4 py-2 text-sm outline-none focus:border-electric" />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-equipment-filter" className="rounded-full border border-line bg-white px-4 py-2 text-sm">
          <option value="">{lang === "fr" ? "Tous les statuts" : "All statuses"}</option>
          {MOON_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/equipment-requests.csv`} data-testid="admin-equipment-csv"
          className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-xs font-bold text-navy-800 hover:border-electric hover:text-electric">
          <Download size={14} /> CSV
        </a>
      </div>
      {visible.length === 0 && <p className="text-sm text-steel" data-testid="admin-equipment-empty">Aucune demande d'équipement pour le moment.</p>}
      <div className="space-y-3">
        {visible.map((r) => (
          <div key={r.request_number} className="rounded-xl border border-line bg-white p-5" data-testid={`admin-equipment-row-${r.request_number}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono text-xs font-bold tracking-wider text-sky-700">{r.request_number}</p>
                <p className="mt-0.5 text-sm font-bold text-navy-800">{r.product}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openMatch(r.request_number)} data-testid={`admin-equipment-match-${r.request_number}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors ${matchFor === r.request_number ? "bg-navy-950 text-white" : "border border-line text-navy-800 hover:border-electric hover:text-electric"}`}>
                  <Radar size={14} /> {lang === "fr" ? "Trouver des fournisseurs" : "Find suppliers"}
                </button>
                <select value={r.status} onChange={(e) => setStatus(r.request_number, e.target.value)} data-testid={`admin-equipment-status-${r.request_number}`}
                  className="rounded border border-line bg-white px-2 py-1 text-xs">
                  {MOON_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3 grid gap-x-6 gap-y-1 text-[13px] text-graphite sm:grid-cols-3">
              <span><strong className="text-navy-800">{r.company}</strong> — {r.contact_name}</span>
              <span><a className="text-electric underline" href={`mailto:${r.email}`}>{r.email}</a>{r.phone ? ` · ${r.phone}` : ""}</span>
              <span>{r.city}, {r.province} · {r.quantity || "1"}× · {r.request_type} · {r.urgency}</span>
              {r.manufacturer && <span>{r.manufacturer}</span>}
              {(r.desired_date || r.budget) && <span>{[r.desired_date, r.budget].filter(Boolean).join(" · ")}</span>}
              {r.file_id && <a className="text-electric underline" href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/uploads/${r.file_id}`} target="_blank" rel="noreferrer" data-testid={`admin-equipment-file-${r.request_number}`}>Pièce jointe</a>}
            </div>
            {r.specs && <p className="mt-2 text-[13px] text-graphite"><strong className="text-navy-800">Specs :</strong> {r.specs}</p>}

            {matchFor === r.request_number && (
              <div className="mt-4 rounded-xl border border-sky-200 bg-ice/60 p-4" data-testid={`match-panel-${r.request_number}`}>
                <p className="text-xs font-bold uppercase tracking-wider text-sky-700">
                  {lang === "fr" ? "Fournisseurs recommandés (compatibilité technique d'abord)" : "Recommended suppliers (technical compatibility first)"}
                </p>
                {matchError && <p role="alert" className="mt-2 text-xs text-red-500">{matchError}</p>}
                {!matches && <p className="mt-2 text-sm text-steel">Recherche…</p>}
                {matches && matches.manual_search_needed && (
                  <p className="mt-3 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-800" data-testid="match-manual-needed">
                    <AlertTriangle size={15} /> {lang === "fr" ? "Aucune correspondance : recherche manuelle nécessaire." : "No match: manual search needed."}
                  </p>
                )}
                {matches && matches.matches.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {matches.matches.map((m) => (
                      <label key={m.supplier_id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 text-[13px]">
                        <input type="checkbox" checked={selected.includes(m.supplier_id)} data-testid={`match-select-${m.supplier_id}`}
                          onChange={(e) => setSelected(e.target.checked ? [...selected, m.supplier_id] : selected.filter((x) => x !== m.supplier_id))}
                          className="mt-0.5 h-4 w-4 accent-electric" />
                        <span>
                          <strong className="text-navy-800">{m.supplier_name}</strong>
                          <span className="ml-2 rounded-full bg-badge px-2 py-0.5 text-[10px] font-bold text-sky-700">score {m.score}</span>
                          <span className="mt-0.5 block text-graphite">{m.reasons.join(" · ")}</span>
                        </span>
                      </label>
                    ))}
                    <button onClick={() => prepareInquiries(r.request_number)} disabled={selected.length === 0} data-testid={`prepare-inquiries-${r.request_number}`}
                      className="rounded-full bg-electric px-5 py-2.5 text-xs font-bold text-white hover:bg-electric-dark disabled:opacity-50">
                      {lang === "fr" ? "Préparer les demandes de prix" : "Prepare price requests"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_SUPPLIER = {
  legal_name: "", trade_name: "", website: "", email: "", phone: "", address: "", city: "", province: "",
  postal_code: "", languages: "français, anglais", territories: "", categories: [], brands: "", products_supported: "",
  mode: "les_deux", delivery_capability: "", interprovincial_shipping: false, sales_contact_name: "", sales_contact_email: "",
  source: "saisie_manuelle", verification_status: "a_verifier", partnership_status: "prospect", internal_notes: "", negotiated_terms: "", opt_out: false,
};

function SuppliersTab() {
  const { t, lang } = useLang();
  const [suppliers, setSuppliers] = useState(null);
  const [search, setSearch] = useState("");
  const [fProvince, setFProvince] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fMode, setFMode] = useState("");
  const [fVerif, setFVerif] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_SUPPLIER);
  const [formError, setFormError] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [stats, setStats] = useState([]);

  const load = () => {
    api.get("/admin/suppliers").then(({ data }) => setSuppliers(data)).catch(() => setSuppliers([]));
    api.get("/admin/supplier-stats").then(({ data }) => setStats(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toggleCat = (slug) => setForm((f) => ({ ...f, categories: f.categories.includes(slug) ? f.categories.filter((c) => c !== slug) : [...f.categories, slug] }));

  const save = async (e) => {
    e.preventDefault();
    setFormError("");
    try {
      await api.post("/admin/suppliers", form);
      setForm(EMPTY_SUPPLIER); setShowForm(false); load();
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setPartStatus = async (id, status) => {
    await api.patch(`/admin/suppliers/${id}`, { status });
    setSuppliers(suppliers.map((s) => (s.id === id ? { ...s, partnership_status: status } : s)));
  };

  const onImport = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/suppliers/import", fd);
      setImportResult(data);
      load();
    } catch (err) {
      setImportResult({ error: formatApiErrorDetail(err.response?.data?.detail) });
    }
  };

  if (!suppliers) return <p className="text-sm text-steel">…</p>;
  const visible = suppliers.filter((s) => {
    if (fProvince && !(s.province || "").toLowerCase().includes(fProvince.toLowerCase())) return false;
    if (fCategory && !(s.categories || []).includes(fCategory)) return false;
    if (fMode && s.mode !== fMode) return false;
    if (fVerif && s.verification_status !== fVerif) return false;
    if (search) {
      const hay = `${s.legal_name} ${s.trade_name || ""} ${s.brands || ""} ${s.email || ""}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const inputCls = "w-full rounded-lg border border-line px-3 py-2 text-sm outline-none focus:border-electric";
  const statFor = (id) => stats.find((x) => x.supplier_id === id);

  return (
    <div data-testid="admin-suppliers-tab">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={lang === "fr" ? "Rechercher (nom, marque, courriel…)" : "Search (name, brand, email…)"} data-testid="admin-suppliers-search"
          className="w-64 rounded-full border border-line bg-white px-4 py-2 text-sm outline-none focus:border-electric" />
        <select value={fProvince} onChange={(e) => setFProvince(e.target.value)} data-testid="admin-suppliers-filter-province" className="rounded-full border border-line bg-white px-4 py-2 text-sm">
          <option value="">{lang === "fr" ? "Province : toutes" : "Province: all"}</option>
          {[...new Set(suppliers.map((s) => s.province).filter(Boolean))].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} data-testid="admin-suppliers-filter-category" className="rounded-full border border-line bg-white px-4 py-2 text-sm">
          <option value="">{lang === "fr" ? "Catégorie : toutes" : "Category: all"}</option>
          {MOON.categories.map((c) => <option key={c.slug} value={c.slug}>{t(c.label)}</option>)}
        </select>
        <select value={fMode} onChange={(e) => setFMode(e.target.value)} data-testid="admin-suppliers-filter-mode" className="rounded-full border border-line bg-white px-4 py-2 text-sm">
          <option value="">{lang === "fr" ? "Vente/location : tous" : "Buy/rent: all"}</option>
          <option value="vente">Vente</option><option value="location">Location</option><option value="les_deux">Les deux</option>
        </select>
        <select value={fVerif} onChange={(e) => setFVerif(e.target.value)} data-testid="admin-suppliers-filter-verif" className="rounded-full border border-line bg-white px-4 py-2 text-sm">
          <option value="">{lang === "fr" ? "Vérification : toutes" : "Verification: all"}</option>
          {VERIF_STATUS.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-line bg-white px-4 py-2 text-xs font-bold text-navy-800 hover:border-electric hover:text-electric" data-testid="admin-suppliers-import">
          <Upload size={14} /> Importer CSV
          <input type="file" accept=".csv" className="hidden" onChange={onImport} />
        </label>
        <button onClick={() => setShowForm(!showForm)} data-testid="admin-suppliers-new"
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white transition-colors ${showForm ? "bg-navy-950" : "bg-electric hover:bg-electric-dark"}`}>
          <Plus size={14} /> {showForm ? (lang === "fr" ? "Fermer le formulaire" : "Close form") : (lang === "fr" ? "Nouveau fournisseur" : "New supplier")}
        </button>
      </div>

      {importResult && (
        <p className="mb-4 rounded-xl border border-sky-200 bg-ice p-4 text-[13px] text-navy-900" data-testid="admin-suppliers-import-result">
          {importResult.error ? `Erreur : ${importResult.error}` :
            lang === "fr"
              ? `Import terminé : ${importResult.imported} fiche(s) créée(s), ${importResult.duplicates_count} doublon(s) ignoré(s), ${importResult.invalid.length} ligne(s) invalide(s).`
              : `Import done: ${importResult.imported} created, ${importResult.duplicates_count} duplicates skipped, ${importResult.invalid.length} invalid lines.`}
        </p>
      )}

      {showForm && (
        <form onSubmit={save} className="mb-6 rounded-xl border border-line bg-white p-6" data-testid="admin-supplier-form">
          <h3 className="font-display text-lg font-bold text-navy-950">{lang === "fr" ? "Nouvelle fiche fournisseur" : "New supplier profile"}</h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <input required placeholder="Nom légal *" data-testid="admin-supplier-legal-name" value={form.legal_name} onChange={set("legal_name")} className={inputCls} />
            <input placeholder="Nom commercial" data-testid="admin-supplier-trade-name" value={form.trade_name} onChange={set("trade_name")} className={inputCls} />
            <input placeholder="Site web" data-testid="admin-supplier-website" value={form.website} onChange={set("website")} className={inputCls} />
            <input placeholder="Courriel" data-testid="admin-supplier-email" value={form.email} onChange={set("email")} className={inputCls} />
            <input placeholder="Téléphone" data-testid="admin-supplier-phone" value={form.phone} onChange={set("phone")} className={inputCls} />
            <input placeholder="Adresse" data-testid="admin-supplier-address" value={form.address} onChange={set("address")} className={inputCls} />
            <input placeholder="Ville" data-testid="admin-supplier-city" value={form.city} onChange={set("city")} className={inputCls} />
            <input placeholder="Province" data-testid="admin-supplier-province" value={form.province} onChange={set("province")} className={inputCls} />
            <input placeholder="Code postal" data-testid="admin-supplier-postal" value={form.postal_code} onChange={set("postal_code")} className={inputCls} />
            <input placeholder="Territoires desservis" data-testid="admin-supplier-territories" value={form.territories} onChange={set("territories")} className={inputCls} />
            <input placeholder="Marques distribuées" data-testid="admin-supplier-brands" value={form.brands} onChange={set("brands")} className={inputCls} />
            <select value={form.mode} data-testid="admin-supplier-mode" onChange={set("mode")} className={inputCls}>
              <option value="vente">Vente</option><option value="location">Location</option><option value="les_deux">Les deux</option>
            </select>
            <input placeholder="Capacité de livraison" data-testid="admin-supplier-delivery" value={form.delivery_capability} onChange={set("delivery_capability")} className={inputCls} />
            <input placeholder="Contact commercial" data-testid="admin-supplier-sales-contact" value={form.sales_contact_name} onChange={set("sales_contact_name")} className={inputCls} />
            <input placeholder="Courriel commercial" data-testid="admin-supplier-sales-email" value={form.sales_contact_email} onChange={set("sales_contact_email")} className={inputCls} />
            <select value={form.verification_status} data-testid="admin-supplier-verif-status" onChange={set("verification_status")} className={inputCls}>
              {VERIF_STATUS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <select value={form.partnership_status} data-testid="admin-supplier-part-status" onChange={set("partnership_status")} className={inputCls}>
              {PART_STATUS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <input placeholder="Source de l'information" data-testid="admin-supplier-source" value={form.source} onChange={set("source")} className={inputCls} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-supplier-cats">
            {MOON.categories.map((c) => (
              <button key={c.slug} type="button" onClick={() => toggleCat(c.slug)} data-testid={`admin-supplier-cat-${c.slug}`}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${form.categories.includes(c.slug) ? "border-electric bg-electric text-white" : "border-slate-200 text-graphite hover:border-electric"}`}>
                {t(c.label)}
              </button>
            ))}
          </div>
          <label className="mt-3 flex items-center gap-2 text-[13px] text-navy-800" data-testid="admin-supplier-optout">
            <input type="checkbox" checked={form.opt_out} onChange={set("opt_out")} className="h-4 w-4 accent-electric" />
            Refus de contact (bloque tout envoi futur — LCSJ/anti-pourriel)
          </label>
          <textarea placeholder="Notes internes" rows={2} data-testid="admin-supplier-notes" value={form.internal_notes} onChange={set("internal_notes")} className={`${inputCls} mt-3`} />
          {formError && <p role="alert" className="mt-3 text-xs text-red-500">{formError}</p>}
          <button type="submit" data-testid="admin-supplier-save" className="mt-4 rounded-full bg-electric px-6 py-2.5 text-xs font-bold text-white hover:bg-electric-dark">
            Enregistrer la fiche
          </button>
        </form>
      )}

      {visible.length === 0 && <p className="text-sm text-steel" data-testid="admin-suppliers-empty">Aucun fournisseur. Ajoutez une fiche manuellement ou importez un CSV.</p>}
      <div className="space-y-3" data-testid="admin-suppliers-list">
        {visible.map((s) => {
          const st = statFor(s.id);
          return (
            <div key={s.id} className="rounded-xl border border-line bg-white p-5" data-testid={`admin-supplier-row-${s.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-navy-800">
                    {s.legal_name}
                    {s.verification_status === "verifie" && <BadgeCheck size={15} className="ml-1.5 inline text-electric" />}
                    {s.opt_out && <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-600">Refus de contact</span>}
                  </p>
                  <p className="mt-0.5 text-[12.5px] text-graphite">
                    {[s.trade_name, s.city && `${s.city}, ${s.province}`, s.mode, (s.categories || []).map((c) => CAT_LABELS[c]?.fr || c).join(", ")].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {st && (
                    <span className="rounded-full bg-paper px-3 py-1.5 text-[11px] text-steel" data-testid={`supplier-metrics-${s.id}`}>
                      {st.reliability_score != null ? `fiabilité ${st.reliability_score}%` : st.total_inquiries > 0 ? `${st.total_inquiries} demande(s), fiabilité: données insuffisantes` : "aucune demande"}
                      {st.avg_response_days != null ? ` · réponse moy. ${st.avg_response_days} j` : ""}
                    </span>
                  )}
                  <select value={s.partnership_status} onChange={(e) => setPartStatus(s.id, e.target.value)} data-testid={`admin-supplier-status-${s.id}`}
                    className="rounded border border-line bg-white px-2 py-1 text-xs">
                    {PART_STATUS.map((st2) => <option key={st2} value={st2}>{st2}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] text-graphite">
                {s.email && <a className="text-electric underline" href={`mailto:${s.email}`}>{s.email}</a>}
                {s.phone && <span>{s.phone}</span>}
                {s.website && <a className="text-electric underline" href={s.website} target="_blank" rel="noreferrer">{s.website}</a>}
                {s.brands && <span>Marques : {s.brands}</span>}
                {s.territories && <span>Territoires : {s.territories}</span>}
                {s.verified_date && <span>Vérifié : {new Date(s.verified_date).toLocaleDateString("fr-CA")}</span>}
                {s.last_contact_date && <span>Dernier contact : {new Date(s.last_contact_date).toLocaleDateString("fr-CA")}</span>}
              </div>
              <button onClick={() => setExpanded(expanded === s.id ? null : s.id)} className="mt-2 text-[12px] font-bold text-electric underline" data-testid={`admin-supplier-history-${s.id}`}>
                {expanded === s.id ? "Masquer l'historique" : `Historique (${(s.history || []).length})`}
              </button>
              {expanded === s.id && (
                <ul className="mt-2 space-y-1 rounded-lg bg-paper p-3 text-[12px] text-graphite" data-testid={`supplier-history-${s.id}`}>
                  {(s.history || []).map((h, i) => <li key={i}>{new Date(h.date).toLocaleString("fr-CA")} — {h.type} : {h.note}</li>)}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PricingTab() {
  const [inquiries, setInquiries] = useState(null);
  const [open, setOpen] = useState(null);
  const [copyState, setCopyState] = useState("");
  useEffect(() => {
    api.get("/admin/supplier-inquiries").then(({ data }) => setInquiries(data)).catch(() => setInquiries([]));
  }, []);
  const patch = async (id, body) => {
    const { data } = await api.patch(`/admin/supplier-inquiries/${id}`, body);
    setInquiries(inquiries.map((i) => (i.id === id ? data.inquiry : i)));
  };
  const send = async (id) => {
    try {
      await api.post(`/admin/supplier-inquiries/${id}/send`);
      setInquiries(inquiries.map((i) => (i.id === id ? { ...i, status: "envoyee", sent_at: new Date().toISOString() } : i)));
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const copy = async (i) => {
    await navigator.clipboard.writeText(i.message);
    setCopyState(i.id);
    setTimeout(() => setCopyState(""), 2000);
  };
  if (!inquiries) return <p className="text-sm text-steel">…</p>;
  if (inquiries.length === 0) return <p className="text-sm text-steel" data-testid="admin-inquiries-empty">Aucune demande de prix. Préparez-en depuis « Demandes MOON » avec le bouton « Trouver des fournisseurs ».</p>;

  const byRequest = {};
  inquiries.forEach((i) => { (byRequest[i.request_number] = byRequest[i.request_number] || []).push(i); });

  return (
    <div className="space-y-8" data-testid="admin-inquiries-tab">
      {Object.entries(byRequest).map(([num, list]) => (
        <section key={num} className="rounded-xl border border-line bg-white p-5" data-testid={`inquiry-group-${num}`}>
          <h3 className="font-mono text-xs font-bold tracking-wider text-sky-700">{num}</h3>
          {list.length > 1 && (
            <div className="mt-3 overflow-x-auto rounded-lg border border-line" data-testid={`comparison-table-${num}`}>
              <table className="w-full min-w-[760px] text-left text-[12px]" data-testid="comparison-table">
                <thead className="bg-paper uppercase tracking-wider text-steel">
                  <tr><th className="px-3 py-2">Fournisseur</th><th className="px-3 py-2">Prix</th><th className="px-3 py-2">Disponibilité</th><th className="px-3 py-2">Délai</th><th className="px-3 py-2">Transport</th><th className="px-3 py-2">Validité</th><th className="px-3 py-2">Statut</th></tr>
                </thead>
                <tbody>
                  {list.map((i) => (
                    <tr key={i.id} className={`border-t border-line ${["sans_reponse", "refusee"].includes(i.status) ? "text-steel line-through" : "text-navy-800"}`}>
                      <td className="px-3 py-2 font-semibold">{i.supplier_name}</td>
                      <td className="px-3 py-2">{i.price || "—"}</td>
                      <td className="px-3 py-2">{i.availability || "—"}</td>
                      <td className="px-3 py-2">{i.lead_time || "—"}</td>
                      <td className="px-3 py-2">{i.shipping_cost || "—"}</td>
                      <td className="px-3 py-2">{i.offer_validity || "—"}</td>
                      <td className="px-3 py-2">{i.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="border-t border-line bg-ice px-3 py-2 text-[11px] text-navy-900">
                {["Les offres non confirmées ou expirées ne constituent pas un prix ferme.", "Unconfirmed or expired offers do not constitute a firm price."][0]}
              </p>
            </div>
          )}
          <div className="mt-4 space-y-3">
            {list.map((i) => (
              <div key={i.id} className="rounded-lg border border-line p-4" data-testid={`inquiry-row-${i.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold text-navy-800">{i.supplier_name} <span className="ml-2 rounded-full bg-paper px-2 py-0.5 text-[10px] text-steel">{i.method}</span></p>
                  <div className="flex items-center gap-2">
                    <select value={i.status} onChange={(e) => patch(i.id, { status: e.target.value })} data-testid={`inquiry-status-${i.id}`} className="rounded border border-line bg-white px-2 py-1 text-xs">
                      {INQ_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {i.follow_up_due && <span className="rounded-full bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-700" data-testid={`inquiry-followup-${i.id}`}>Relance : {i.follow_up_due}</span>}
                  </div>
                </div>
                <button onClick={() => setOpen(open === i.id ? null : i.id)} className="mt-2 text-[12px] font-bold text-electric underline" data-testid={`inquiry-toggle-${i.id}`}>
                  {open === i.id ? "Masquer les détails" : "Message et réponse"}
                </button>
                {open === i.id && (
                  <div className="mt-3 space-y-3">
                    <div className="rounded-lg bg-paper p-3">
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-steel">Message préparé (validation requise avant envoi)</p>
                        <button onClick={() => copy(i)} className="inline-flex items-center gap-1 text-[11px] font-bold text-electric underline" data-testid={`inquiry-copy-${i.id}`}>
                          <Copy size={12} /> {copyState === i.id ? "Copié !" : "Copier"}
                        </button>
                      </div>
                      <pre className="mt-2 whitespace-pre-wrap font-sans text-[12.5px] leading-relaxed text-navy-900" data-testid={`inquiry-message-${i.id}`}>{i.message}</pre>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {i.status === "en_attente_validation" && (
                        <button onClick={() => patch(i.id, { status: "prete_a_envoyer" })} data-testid={`inquiry-validate-${i.id}`}
                          className="rounded-full border border-electric px-4 py-2 text-[11px] font-bold text-electric hover:bg-electric hover:text-white">
                          Valider (prête à envoyer)
                        </button>
                      )}
                      {["prete_a_envoyer", "relance_necessaire"].includes(i.status) && (
                        <button onClick={() => send(i.id)} data-testid={`inquiry-send-${i.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-electric px-4 py-2 text-[11px] font-bold text-white hover:bg-electric-dark">
                          <Send size={12} /> Envoyer par courriel
                        </button>
                      )}
                      {i.sent_at && <span className="self-center text-[11px] text-steel">Envoyée le {new Date(i.sent_at).toLocaleString("fr-CA")}</span>}
                    </div>
                    <div className="grid gap-2 sm:grid-cols-3">
                      <input placeholder="Prix proposé" value={i.price || ""} onChange={(e) => setInquiries(inquiries.map((x) => (x.id === i.id ? { ...x, price: e.target.value } : x)))}
                        onBlur={(e) => patch(i.id, { price: e.target.value })} data-testid={`inquiry-price-${i.id}`} className="rounded-lg border border-line px-3 py-2 text-xs" />
                      <input placeholder="Disponibilité déclarée" value={i.availability || ""} onChange={(e) => setInquiries(inquiries.map((x) => (x.id === i.id ? { ...x, availability: e.target.value } : x)))}
                        onBlur={(e) => patch(i.id, { availability: e.target.value })} data-testid={`inquiry-availability-${i.id}`} className="rounded-lg border border-line px-3 py-2 text-xs" />
                      <input placeholder="Délai annoncé" value={i.lead_time || ""} onChange={(e) => setInquiries(inquiries.map((x) => (x.id === i.id ? { ...x, lead_time: e.target.value } : x)))}
                        onBlur={(e) => patch(i.id, { lead_time: e.target.value })} data-testid={`inquiry-leadtime-${i.id}`} className="rounded-lg border border-line px-3 py-2 text-xs" />
                      <input placeholder="Frais de transport" value={i.shipping_cost || ""} onChange={(e) => setInquiries(inquiries.map((x) => (x.id === i.id ? { ...x, shipping_cost: e.target.value } : x)))}
                        onBlur={(e) => patch(i.id, { shipping_cost: e.target.value })} data-testid={`inquiry-shipping-${i.id}`} className="rounded-lg border border-line px-3 py-2 text-xs" />
                      <input placeholder="Validité de l'offre" value={i.offer_validity || ""} onChange={(e) => setInquiries(inquiries.map((x) => (x.id === i.id ? { ...x, offer_validity: e.target.value } : x)))}
                        onBlur={(e) => patch(i.id, { offer_validity: e.target.value })} data-testid={`inquiry-validity-${i.id}`} className="rounded-lg border border-line px-3 py-2 text-xs" />
                      <input type="date" placeholder="Relance prévue" value={i.follow_up_due || ""} onChange={(e) => patch(i.id, { follow_up_due: e.target.value })} data-testid={`inquiry-followupdate-${i.id}`} className="rounded-lg border border-line px-3 py-2 text-xs" />
                    </div>
                    <textarea placeholder="Réponse du fournisseur / notes de comparaison" rows={3} value={i.response_text || ""}
                      onChange={(e) => setInquiries(inquiries.map((x) => (x.id === i.id ? { ...x, response_text: e.target.value } : x)))}
                      onBlur={(e) => patch(i.id, { response_text: e.target.value, status: i.status === "envoyee" ? "reponse_recue" : i.status })}
                      data-testid={`inquiry-response-${i.id}`} className="w-full rounded-lg border border-line px-3 py-2 text-xs" />
                    <p className="text-[11px] text-steel">
                      Note : le message envoyé au fournisseur ne contient jamais le budget du client ni ses coordonnées.
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

const emptyArticle = { title_fr: "", title_en: "", excerpt_fr: "", excerpt_en: "", body_fr: "", body_en: "", category: "", published: true };

function ArticlesTab() {
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/articles").then(({ data }) => setArticles(data)).catch(() => setArticles([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editing) {
        await api.put(`/admin/articles/${editing}`, form);
      } else {
        await api.post("/admin/articles", form);
      }
      setEditing(null); setForm(emptyArticle); load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/articles/${id}`);
    load();
  };

  const edit = (a) => {
    setEditing(a.id);
    setForm({
      title_fr: a.title_fr || "", title_en: a.title_en || "", excerpt_fr: a.excerpt_fr || "", excerpt_en: a.excerpt_en || "",
      body_fr: a.body_fr || "", body_en: a.body_en || "", category: a.category || "", published: a.published,
    });
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-navy-950">Articles</h2>
          <button onClick={() => { setEditing(null); setForm(emptyArticle); }} data-testid="admin-article-new"
            className="inline-flex items-center gap-1.5 rounded-full bg-navy-950 px-4 py-2 text-xs font-bold text-white hover:bg-electric">
            <Plus size={14} /> Nouveau
          </button>
        </div>
        {!articles && <p className="mt-4 text-sm text-steel">…</p>}
        <ul className="mt-4 space-y-2">
          {(articles || []).map((a) => (
            <li key={a.id} className="flex items-start justify-between gap-3 rounded-lg border border-line bg-white p-4" data-testid={`admin-article-item-${a.id}`}>
              <div>
                <p className="text-sm font-semibold text-navy-800">{a.title_fr}</p>
                <p className="mt-0.5 text-xs text-steel">{a.category} · {a.published ? "publié" : "brouillon"} · /{a.slug}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button onClick={() => edit(a)} data-testid={`admin-article-edit-${a.id}`} className="rounded border border-line px-2.5 py-1 text-xs font-semibold text-navy-800 hover:border-electric hover:text-electric">Modifier</button>
                <button onClick={() => remove(a.id)} data-testid={`admin-article-delete-${a.id}`} aria-label="Supprimer" className="rounded border border-red-200 p-1.5 text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-7">
        <form onSubmit={save} className="rounded-xl border border-line bg-white p-7" data-testid="admin-article-form">
          <h2 className="font-display text-xl font-bold text-navy-950">{editing ? "Modifier l'article" : "Nouvel article"}</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <input required placeholder="Titre (FR)" data-testid="admin-article-title-fr" value={form.title_fr} onChange={set("title_fr")} className="rounded-lg border border-line px-3.5 py-2.5 text-sm outline-none focus:border-electric" />
            <input placeholder="Title (EN)" data-testid="admin-article-title-en" value={form.title_en} onChange={set("title_en")} className="rounded-lg border border-line px-3.5 py-2.5 text-sm outline-none focus:border-electric" />
            <input required placeholder="Catégorie" data-testid="admin-article-category" value={form.category} onChange={set("category")} className="rounded-lg border border-line px-3.5 py-2.5 text-sm outline-none focus:border-electric" />
            <label className="flex items-center gap-2 text-sm text-navy-800">
              <input type="checkbox" checked={form.published} onChange={set("published")} data-testid="admin-article-published" className="h-4 w-4 accent-electric" /> Publié
            </label>
            <textarea required placeholder="Extrait (FR)" rows={2} data-testid="admin-article-excerpt-fr" value={form.excerpt_fr} onChange={set("excerpt_fr")} className="rounded-lg border border-line px-3.5 py-2.5 text-sm outline-none focus:border-electric sm:col-span-2" />
            <textarea placeholder="Excerpt (EN)" rows={2} data-testid="admin-article-excerpt-en" value={form.excerpt_en} onChange={set("excerpt_en")} className="rounded-lg border border-line px-3.5 py-2.5 text-sm outline-none focus:border-electric sm:col-span-2" />
            <textarea required placeholder="Contenu (FR) — séparer les paragraphes par une ligne vide" rows={9} data-testid="admin-article-body-fr" value={form.body_fr} onChange={set("body_fr")} className="rounded-lg border border-line px-3.5 py-2.5 text-sm outline-none focus:border-electric sm:col-span-2" />
            <textarea placeholder="Body (EN) — separate paragraphs with a blank line" rows={9} data-testid="admin-article-body-en" value={form.body_en} onChange={set("body_en")} className="rounded-lg border border-line px-3.5 py-2.5 text-sm outline-none focus:border-electric sm:col-span-2" />
          </div>
          {error && <p role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          <button type="submit" data-testid="admin-article-save" className="mt-5 rounded-full bg-electric px-7 py-3 text-sm font-bold text-white hover:bg-electric-dark">
            {editing ? "Enregistrer" : "Publier"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("equipment");
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => navigate("/admin"));
  }, [navigate]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin");
  };

  const tabs = [
    { key: "equipment", label: "Demandes MOON", icon: PackageSearch },
    { key: "pricing", label: "Demandes de prix", icon: Send },
    { key: "suppliers", label: "Fournisseurs", icon: Users },
    { key: "leads", label: "Consultations", icon: Mail },
    { key: "articles", label: "Articles", icon: FileText },
  ];

  return (
    <main className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <header className="border-b border-line bg-white">
        <div className="mx-auto flex max-w-[1320px] flex-wrap items-center justify-between gap-4 px-5 py-4 md:px-8">
          <div className="flex flex-wrap items-center gap-4">
            <span className="font-display text-xl font-bold text-navy-950">EMP — Admin</span>
            <nav className="flex flex-wrap gap-1" aria-label="Sections admin">
              {tabs.map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === key ? "bg-navy-950 text-white" : "text-graphite hover:text-navy-950"}`}>
                  <Icon size={14} /> {label}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            {me && <span className="hidden text-xs text-steel sm:block" data-testid="admin-user-email">{me.email}</span>}
            <a href="/crm" data-testid="admin-link-crm" className="inline-flex items-center gap-1.5 rounded-full bg-electric px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-electric-dark">
              <Radar size={14} /> CRM
            </a>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 text-xs font-bold text-navy-800 hover:border-red-300 hover:text-red-600">
              <LogOut size={14} /> Quitter
            </button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-[1320px] px-5 py-10 md:px-8">
        {tab === "equipment" && <EquipmentTab />}
        {tab === "pricing" && <PricingTab />}
        {tab === "suppliers" && <SuppliersTab />}
        {tab === "leads" && <LeadsTab />}
        {tab === "articles" && <ArticlesTab />}
      </div>
    </main>
  );
}
